import { Col } from "@amzn/stencil-react-components/layout";
import {
  Link,
  LinkTextDecorationOptions,
} from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import LaptopLoginQr from "@/assets/images/laptopLoginQr.svg";
import { OPERATING_SYSTEM } from "@/helpers/os-helper";

const WINDOWS_LOGIN_QR_URL =
  "https://firstaid.it.a2z.com/laptoploginhelp?flow=existing&os=windows";

const PORTAL_LINK_STYLE = {
  className: "wizard-link",
  display: "inline",
  fontWeight: "normal",
  rel: "noopener noreferrer",
  target: "_blank",
  textDecoration: LinkTextDecorationOptions.None,
} as const;

/**
 * QR block shown at the top of the Windows post-login instructions. The QR
 * encodes a deep link into FirstAid that skips the /laptoploginhelp landing
 * selection screen and lands directly on the Windows login directions, so a
 * just-onboarded hire can scan it with their phone to see how to sign in with
 * their new ANT account. The URL is inlined in the CTA copy for anyone who
 * prefers to type it, and the muted email callout is the pre-QR path preserved
 * for anyone who can't scan and can't visit the URL.
 */
function WindowsLoginQr() {
  const intl = useIntl();
  const altText = intl.formatMessage({
    id: "allStepsCompleted-win-postLoginQrAlt",
  });

  return (
    // Two-layer Col: outer centers the block horizontally within whatever its
    // parent provides, inner constrains the centered content to a compact
    // width so the CTA doesn't spill past the title/steps' left edge. Result:
    // CTA + QR + fallback all appear centered inside the same visual column
    // that the "Great job!" / "MANDATORY NEXT STEPS" / step list occupy.
    <Col padding="0.8rem 0" alignItems="center" dataTestId="postLoginQrBlock">
      <Col gridGap="S400" alignItems="center" maxWidth={720}>
        <Text
          fontWeight="bold"
          textAlign="center"
          dataTestId="postLoginQrHeading"
        >
          <FormattedMessage
            id="allStepsCompleted-win-postLoginQrHeading"
            values={{
              qrUrl: (
                <Link
                  {...PORTAL_LINK_STYLE}
                  color="color.link.primary-default"
                  href={WINDOWS_LOGIN_QR_URL}
                >
                  {WINDOWS_LOGIN_QR_URL}
                </Link>
              ),
            }}
          />
        </Text>
        <img
          src={LaptopLoginQr}
          alt={altText}
          data-test-id="postLoginQrImage"
          width={200}
          height={200}
        />
        <Text
          color="color.text.secondary"
          textAlign="center"
          dataTestId="postLoginQrEmailFallback"
        >
          <FormattedMessage id="allStepsCompleted-win-postLoginQrEmailFallback" />
        </Text>
      </Col>
    </Col>
  );
}

function MacOrderedItems() {
  return (
    <>
      <li>
        <Text>
          <FormattedMessage id="allStepsCompleted-mac-postLoginStep1" />
        </Text>
        <ol type="a">
          <li>
            <Text>
              <FormattedMessage id="allStepsCompleted-mac-postLoginStep1-substep1" />
            </Text>
          </li>
        </ol>
      </li>
      <li>
        <Text>
          <FormattedMessage id="allStepsCompleted-mac-postLoginStep2" />
        </Text>
      </li>
    </>
  );
}

/**
 * Show a list of instructions for completing the post-login setup on day-1.
 *
 * Windows renders a QR-first layout (WindowsLoginQr) followed by two ordered
 * steps (restart + productivity-apps link). The QR replaces the old "check
 * your personal email for instructions on how to log in after the reboot"
 * step; the email path is preserved as a smaller muted fallback callout inside
 * the QR block for anyone who can't scan.
 *
 * Mac renders the pre-existing 3-step layout unchanged (sign out of tokenadmin
 * → check email → productivity-apps link). The current-employee Mac path on
 * FirstAid is an identity-verification flow, not a simple directions page, so
 * a QR deep link into it would land somewhere materially different; keeping
 * Mac as-is is deliberate.
 */
export default function PostLoginInstructions({
  os,
}: {
  os: string;
}): JSX.Element {
  const isMac = os === OPERATING_SYSTEM.MAC;
  // Short links maintained by ITS Comms; these match the approved day-1
  // completion email templates. The former /postlogin/newhire/{mac,win}
  // path 404s for new hires (V2420823918).
  const newHirePortalLinkUrl = `https://my.it.a2z.com/nho-${
    isMac ? "mac" : "win"
  }`;

  return (
    <Col>
      {!isMac && <WindowsLoginQr />}
      <ol data-test-id={`postLogin${isMac ? "Mac" : "Wins"}Steps`}>
        <Col gridGap="S200" padding="0.8rem 0">
          {isMac ? (
            <MacOrderedItems />
          ) : (
            <li>
              <Text>
                <FormattedMessage id="allStepsCompleted-win-postLoginStep1" />
              </Text>
            </li>
          )}
          <li data-test-id="postLoginStep3">
            <Text>
              <FormattedMessage
                id="allStepsCompleted-postLoginStep3"
                values={{
                  newHirePortalLinkUrl: (
                    <Link {...PORTAL_LINK_STYLE} href={newHirePortalLinkUrl}>
                      {newHirePortalLinkUrl}
                    </Link>
                  ),
                }}
              />
            </Text>
          </li>
          <Text fontWeight="bold">
            <FormattedMessage id="allStepsCompleted-reminder" />
          </Text>
        </Col>
      </ol>
    </Col>
  );
}
